/**
 * lib/bankProof.ts
 * ------------------------------------------------------------------
 * THE BANK RECONCILIATION PROPER.
 *
 * What LKC already has (BankReconciliation) is a RECEIPTS IMPORTER: it
 * reads money-IN lines from a statement and matches them to open
 * invoices. That is useful, and it is not a reconciliation. It never
 * looks at money going out, never reads the bank's closing balance, and
 * therefore cannot answer the only question a reconciliation exists for:
 *
 *     does what our ledger says is in the bank agree with what the bank
 *     says is in the bank?
 *
 * Until that is answered, a missing payment, a duplicated receipt, a bank
 * charge nobody captured and an unpresented cheque all sit in the
 * accounts indefinitely — each of them invisible, because every screen
 * that could show one is internally consistent.
 *
 * THE FOUR-LINE PROOF, which is the standard statement and the thing an
 * auditor expects to be handed:
 *
 *     balance per the BANK statement
 *   − payments in our books not yet on the statement   (unpresented)
 *   + receipts in our books not yet on the statement   (uncredited)
 *   − statement lines we have not captured at all      (unrecorded)
 *   = balance per OUR ledger
 *
 * THE THIRD LINE IS THE ONE THAT MATTERS. Unpresented and uncredited
 * items are timing: they will clear. A statement line the school has
 * never recorded is not timing — it is a transaction the books do not
 * know about, and it is where the bank charge, the unauthorised debit
 * order and the payment nobody captured all live.
 *
 * PURE. Balances and lines in, a proof out.
 * ------------------------------------------------------------------ */
import { round2 } from '@/lib/financeCore';

export const BANK_PROOF_COLLECTION = 'financeBankProofs';

/** A line off the bank's own statement. */
export interface StatementLine {
  id: string;
  date: string;
  description: string;
  /** Positive is money IN, negative is money OUT — the bank's sign. */
  amount: number;
  reference?: string;
  /** Set once somebody has said which ledger entry it is. */
  matchedJournalId?: string;
  /** Set when the school has decided it needs capturing. */
  capturedAs?: string;
}

/** Something in our books the bank has not shown yet. */
export interface OutstandingItem {
  id: string;
  date: string;
  description: string;
  /** Always positive; `kind` says which way it goes. */
  amount: number;
  kind: 'unpresented' | 'uncredited';
  reference?: string;
}

export interface BankProof {
  id: string;
  /** The account being proved. */
  accountCode: string;
  /** The statement's own closing date and balance — what the bank says. */
  statementDate: string;
  statementBalance: number;
  /** What the ledger says on that date. */
  ledgerBalance: number;
  outstanding: OutstandingItem[];
  /** Statement lines with no entry in our books. */
  unrecorded: StatementLine[];
  preparedBy?: string;
  preparedByName?: string;
  preparedAt?: string;
  /** Signed off by somebody other than whoever prepared it. */
  reviewedBy?: string;
  reviewedByName?: string;
  reviewedAt?: string;
  note?: string;
}

export interface ProofResult {
  statementBalance: number;
  unpresented: number;
  uncredited: number;
  unrecorded: number;
  /** What the ledger SHOULD say once the four lines are applied. */
  expectedLedger: number;
  /** What it actually says. */
  actualLedger: number;
  /** expected − actual. Nil on a reconciled account. */
  difference: number;
  reconciled: boolean;
  /** Plain words for whoever has to act on it. */
  summary: string;
}

/**
 * Work the proof.
 *
 * The sign convention is the school's, not the bank's: an unpresented
 * PAYMENT reduces the bank balance when it clears, so it is subtracted;
 * an uncredited RECEIPT increases it, so it is added. Getting these the
 * wrong way round produces a proof that is out by exactly twice the
 * item, which is the classic way a reconciliation is "nearly right".
 */
export function proveBank(
  statementBalance: number,
  ledgerBalance: number,
  outstanding: OutstandingItem[],
  unrecorded: StatementLine[],
): ProofResult {
  const unpresented = round2(outstanding
    .filter((o) => o.kind === 'unpresented')
    .reduce((n, o) => n + Math.abs(Number(o.amount) || 0), 0));

  const uncredited = round2(outstanding
    .filter((o) => o.kind === 'uncredited')
    .reduce((n, o) => n + Math.abs(Number(o.amount) || 0), 0));

  /* Signed as the bank showed them: a charge is negative and reduces
     what the books should hold once captured. */
  const unrecordedNet = round2(unrecorded.reduce((n, l) => n + (Number(l.amount) || 0), 0));

  const expectedLedger = round2(
    round2(statementBalance) - unpresented + uncredited + unrecordedNet,
  );
  const actual = round2(ledgerBalance);
  const difference = round2(expectedLedger - actual);
  const reconciled = Math.abs(difference) < 0.005;

  return {
    statementBalance: round2(statementBalance),
    unpresented,
    uncredited,
    unrecorded: unrecordedNet,
    expectedLedger,
    actualLedger: actual,
    difference,
    reconciled,
    summary: reconciled
      ? 'The bank agrees with the ledger.'
      : `${Math.abs(difference).toFixed(2)} unexplained. Every timing difference has been `
        + 'accounted for, so this is a transaction one side has and the other does not — '
        + 'not something that will clear on its own.',
  };
}

/**
 * Statement lines nobody has matched or captured.
 *
 * THE PART OF A RECONCILIATION WITH ACTUAL FINDINGS IN IT. An unmatched
 * line is not a timing difference: the bank has moved money the books do
 * not know about. Bank charges, debit orders nobody set up, a payment
 * captured against the wrong account.
 */
export function unmatchedLines(lines: StatementLine[]): StatementLine[] {
  return lines
    .filter((l) => !l.matchedJournalId && !l.capturedAs)
    .sort((a, b) => (a.date ?? '').localeCompare(b.date ?? ''));
}

/**
 * How long an outstanding item has been outstanding.
 *
 * A cheque unpresented for a week is normal. One unpresented for six
 * months was never banked, and it is holding the reconciliation together
 * by pretending money will leave that never will.
 */
export function staleOutstanding(
  items: OutstandingItem[],
  asOf: Date = new Date(),
  days = 90,
): (OutstandingItem & { ageDays: number })[] {
  const today = asOf.getTime();
  return items
    .map((o) => ({
      ...o,
      ageDays: Math.max(0, Math.round((today - Date.parse(o.date)) / 86400000)),
    }))
    .filter((o) => o.ageDays >= days)
    .sort((a, b) => b.ageDays - a.ageDays);
}

/**
 * Whether this proof may be signed off.
 *
 * A RECONCILIATION SIGNED BY THE PERSON WHO PREPARED IT IS NOT A REVIEW.
 * The whole value of the second signature is a second pair of eyes, and
 * it is the one control in a reconciliation that catches a deliberate
 * error rather than an accidental one.
 */
export function reviewProblem(
  proof: Pick<BankProof, 'preparedBy' | 'reviewedBy'>,
  reviewerUid: string,
  result: ProofResult,
): string | null {
  if (!proof.preparedBy) return 'Nobody has prepared this reconciliation yet.';
  if (proof.reviewedBy) return 'This reconciliation has already been reviewed.';
  if (proof.preparedBy === reviewerUid) {
    return 'You prepared this reconciliation, so you cannot also review it. '
      + 'A second pair of eyes is the whole point of the review.';
  }
  if (!result.reconciled) {
    return `It does not reconcile — ${Math.abs(result.difference).toFixed(2)} is unexplained. `
      + 'Signing that off records a proof that does not prove anything.';
  }
  return null;
}
